import { ITeamProfile } from '@shared/services/teamProfiles.service';
import { IWorktask, WorktaskService } from '@shared/services/worktask.service';
import { getActiveConfiguration, getWorkDayForDate } from './create-shift-note/team-profile.helpers';
import { ITimestamp } from './mes-shift.service';

export interface IShiftTimeResult {
	payTimeMinutes: number;
	overtimeMinutes: number;
	absenceMinutes: number;
}

export interface IShiftTimeInput {
	timestamps: ITimestamp[];
	correctedStartTime: string | null;
	correctedEndTime: string | null;
	breakMinutes?: number;
}

/** Converts total minutes to H:mm format. E.g. 90 → "1:30", 0 → "0:00", 125 → "2:05" */
export function formatMinutesAsHMM(minutes: number): string {
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	return `${hours}:${mins.toString().padStart(2, '0')}`;
}

/** Resolves norm time in minutes from a team profile configuration for the given shift date's weekday. Returns 0 if team profile is null, no active configuration exists, or the day is not a work day. */
export function resolveNormTimeMinutes(teamProfile: ITeamProfile | null, shiftDate: Date): number {
	if (!teamProfile) {
		return 0;
	}

	const activeConfig = getActiveConfiguration(teamProfile.configurations, shiftDate);
	if (!activeConfig) {
		return 0;
	}

	const workDay = getWorkDayForDate(activeConfig.weekDays, shiftDate);
	if (!workDay.isWorkDay || !workDay.workTime) {
		return 0;
	}

	const workDuration = getIntervalDurationMinutes(workDay.workTime.startTime, workDay.workTime.endTime);

	const breakDuration = (workDay.breaks || []).reduce((sum, brk) => {
		return sum + getIntervalDurationMinutes(brk.startTime, brk.endTime);
	}, 0);

	return Math.max(0, workDuration - breakDuration);
}

/** Resolves the total break duration in minutes from the same team profile configuration and weekday used for norm time. */
export function resolveBreakMinutes(teamProfile: ITeamProfile | null, shiftDate: Date): number {
	if (!teamProfile) {
		return 0;
	}

	const activeConfig = getActiveConfiguration(teamProfile.configurations, shiftDate);
	if (!activeConfig) {
		return 0;
	}

	const workDay = getWorkDayForDate(activeConfig.weekDays, shiftDate);
	if (!workDay.isWorkDay || !workDay.workTime) {
		return 0;
	}

	return (workDay.breaks || []).reduce((sum, brk) => {
		return sum + getIntervalDurationMinutes(brk.startTime, brk.endTime);
	}, 0);
}

/**
 * Classifies timestamp durations into pay time, overtime, and absence.
 *
 * Algorithm: 1. If correctedStartTime or correctedEndTime is null, return all zeros. 2. Sort timestamps by dateTimeOffset ascending. 3. For each consecutive timestamp pair, compute effective duration clamped to corrected boundaries. 4. Classify: off-worktask → skip, salarytype tag → absence, otherwise → working time. 5.
 * payableWorkingMinutes = max(0, workingMinutes - breakMinutes), payableAbsenceMinutes = absenceMinutes minus breaks not covered by working time 6. payTimeMinutes = min(payableWorkingMinutes, max(0, normTimeMinutes - absenceMinutes)) 7. overtimeMinutes = max(0, payableWorkingMinutes - payTimeMinutes)
 */
export function calculateShiftTimes(input: IShiftTimeInput, worktasks: IWorktask[], normTimeMinutes: number): IShiftTimeResult {
	if (!input.correctedStartTime || !input.correctedEndTime) {
		return { payTimeMinutes: 0, overtimeMinutes: 0, absenceMinutes: 0 };
	}

	if (input.timestamps.length < 2) {
		return { payTimeMinutes: 0, overtimeMinutes: 0, absenceMinutes: 0 };
	}

	// A shift must be ended (have an off-worktask timestamp) to calculate times
	const hasOffTimestamp = input.timestamps.some((ts) => ts.workTaskId === WorktaskService.offWorkTaskId);
	if (!hasOffTimestamp) {
		return { payTimeMinutes: 0, overtimeMinutes: 0, absenceMinutes: 0 };
	}

	// A shift must have at least one non-off timestamp (actual stamping) to calculate times
	const hasNonOffTimestamp = input.timestamps.some((ts) => ts.workTaskId !== WorktaskService.offWorkTaskId);
	if (!hasNonOffTimestamp) {
		return { payTimeMinutes: 0, overtimeMinutes: 0, absenceMinutes: 0 };
	}

	const correctedStart = new Date(input.correctedStartTime).getTime();
	const correctedEnd = new Date(input.correctedEndTime).getTime();
	if (!Number.isFinite(correctedStart) || !Number.isFinite(correctedEnd) || correctedEnd <= correctedStart) {
		return { payTimeMinutes: 0, overtimeMinutes: 0, absenceMinutes: 0 };
	}

	const sorted = [...input.timestamps].sort((a, b) => new Date(a.dateTimeOffset).getTime() - new Date(b.dateTimeOffset).getTime());
	const nonOffTimestamps = sorted.filter((timestamp) => timestamp.workTaskId !== WorktaskService.offWorkTaskId);
	const firstNonOffTimestampTime = new Date(nonOffTimestamps[0].dateTimeOffset).getTime();
	const lastRawTimestampTime = new Date(sorted[sorted.length - 1].dateTimeOffset).getTime();

	const worktaskMap = new Map<string, IWorktask>();
	for (const wt of worktasks) {
		worktaskMap.set(wt.id, wt);
	}

	let workingMinutes = 0;
	let absenceMinutes = 0;

	if (correctedStart < firstNonOffTimestampTime) {
		const shadowEnd = Math.min(firstNonOffTimestampTime, correctedEnd);
		workingMinutes += Math.max(0, shadowEnd - correctedStart) / 60000;
	}

	for (let i = 0; i < sorted.length; i++) {
		const timestamp = sorted[i];
		const isLast = i === sorted.length - 1;

		// Skip off-worktask timestamps
		if (timestamp.workTaskId === WorktaskService.offWorkTaskId) {
			continue;
		}

		const tsStart = new Date(timestamp.dateTimeOffset).getTime();
		const tsEnd = isLast ? correctedEnd : new Date(sorted[i + 1].dateTimeOffset).getTime();

		const effectiveStart = Math.max(tsStart, correctedStart);
		const effectiveEnd = Math.min(tsEnd, correctedEnd);
		const durationMs = Math.max(0, effectiveEnd - effectiveStart);
		const durationMinutes = durationMs / 60000;

		const worktask = worktaskMap.get(timestamp.workTaskId as string);
		const hasSalaryType = worktask?.tags?.['salarytype'] && worktask.tags['salarytype'] !== '';

		if (hasSalaryType) {
			absenceMinutes += durationMinutes;
		} else {
			workingMinutes += durationMinutes;
		}
	}

	if (correctedEnd > lastRawTimestampTime) {
		const shadowStart = Math.max(lastRawTimestampTime, correctedStart);
		workingMinutes += Math.max(0, correctedEnd - shadowStart) / 60000;
	}

	const breakMinutes = Math.max(0, input.breakMinutes ?? 0);
	const payableWorkingMinutes = Math.max(0, workingMinutes - breakMinutes);
	const payableAbsenceMinutes = Math.max(0, absenceMinutes - Math.max(0, breakMinutes - workingMinutes));
	const regularCapacityMinutes = Math.max(0, normTimeMinutes - absenceMinutes);
	const payTimeMinutes = Math.min(payableWorkingMinutes, regularCapacityMinutes);
	const overtimeMinutes = Math.max(0, payableWorkingMinutes - payTimeMinutes);

	return {
		payTimeMinutes: Math.round(payTimeMinutes),
		overtimeMinutes: Math.round(overtimeMinutes),
		absenceMinutes: Math.round(payableAbsenceMinutes),
	};
}

/** Parses a time string in HH:mm format to total minutes since midnight. */
function parseTimeToMinutes(time: string): number {
	const [hours, minutes] = time.split(':').map(Number);
	return hours * 60 + minutes;
}

function getIntervalDurationMinutes(startTime: string, endTime: string): number {
	const startMinutes = parseTimeToMinutes(startTime);
	const endMinutes = parseTimeToMinutes(endTime);

	if (endMinutes >= startMinutes) {
		return endMinutes - startMinutes;
	}

	return 24 * 60 - startMinutes + endMinutes;
}
